<template>
    <div>
        <div class="container-fluid mt-2 mb-5">
            <div class="row">
                <div class="col-md-12">
                    <h3><span class="material-icons md-24 text-main-gradient">build</span> Herramientas</h3>
                </div>
                <div class="col-md-6 mt-3">
                    <p>
                        Bienvenido a la sección de <strong class="fw-bold">herramientas</strong>. Aquí puedes gestionar tu <strong class="fw-bold">inventario de medicamentos</strong>, 
                        configurar tus <strong class="fw-bold">consultas</strong> (a domicilio, en consultorio, virtual o en hospital) y realizar un 
                        seguimiento detallado de los <strong class="fw-bold">estudios solicitados</strong> a tus pacientes. Además, cuentas con una 
                        tabla para registrar los <strong class="fw-bold">tipos de estudios</strong> que puedes medir en tus pacientes, los cuales se 
                        podrán visualizar en <strong class="fw-bold">gráficas</strong> para analizar la evolución de cada caso.
                    </p>
                </div>

                <div class="col-md-12 mt-5">
                    <h3><span class="material-icons md-24 text-main-gradient">schedule</span> Horarios</h3>
                </div>
                <div class="col-md-12 mt-2 mb-3">
                    <horarios :personal_medico_id="currentUser?.mainId" @notify="notify" />
                </div>

                <div class="col-md-12 mt-5">
                    <h3><span class="material-icons md-24 text-main-gradient">medication</span> Medicamentos</h3>
                </div>
                <div class="col-md-12 mt-2 mb-3">
                    <search-medicamentos :main-id="currentUser?.mainId" @notify="notify"
                        @select="handleMedicamentoSelected" />
                </div>
                <div class="col-md-4 mt-2 mb-4">
                    <h4>{{ medicamentoSeleccionado ? 'Editar Medicamento' : 'Nuevo Medicamento' }}</h4>
                    <p class="text-muted">Administra tu inventario de medicamentos desde aquí. Al realizar una consulta, podrás acceder rápidamente a estos medicamentos sin necesidad de escribir demasiado.</p>
                    <div class="card">
                        <div class="card-body">
                            <p class="text-muted mt-2">Los campos marcados con <span class="text-danger">*</span> son
                                obligatorios.</p>
                            <div class="row">
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Nombre del Medicamento <span
                                            class="text-danger">*</span></label>
                                    <input type="text" class="form-control"
                                        v-model.trim="nuevoMedicamento.nombre_medicamento" required>
                                    <small class="form-text text-muted">Ejemplo: Paracetamol</small>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Nombre Comercial <span
                                            class="text-danger">*</span></label>
                                    <input type="text" class="form-control"
                                        v-model.trim="nuevoMedicamento.nombre_comercial" required>
                                    <small class="form-text text-muted">Ejemplo: Tylenol</small>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Presentación <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model.trim="nuevoMedicamento.presentacion"
                                        required>
                                    <small class="form-text text-muted">Ejemplo: Tabletas de 500mg</small>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Dosis Usual <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model.trim="nuevoMedicamento.dosis_usual"
                                        required>
                                    <small class="form-text text-muted">Ejemplo: 1 tableta cada 8 horas</small>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Vía de Administración <span
                                            class="text-danger">*</span></label>
                                    <input type="text" class="form-control"
                                        v-model.trim="nuevoMedicamento.via_administracion" required>
                                    <small class="form-text text-muted">Ejemplo: Oral</small>
                                </div>
                                <div class="col-md-12 text-center" v-if="userStats !== 1">
                                    <vs-button  v-if="isEditing" @click="actualizarMedicamento" color="rgb(59,222,200)"
                                        gradient block>
                                        🔄 Actualizar Medicamento
                                    </vs-button>
                                    <vs-button  v-else @click="crearMedicamento" color="rgb(59,222,200)" gradient block>
                                        💾 Guardar Medicamento
                                    </vs-button>

                                    <div class="d-flex justify-content-center gap-2">
                                        <a class="smallText text-center" v-if="isEditing" @click="resetForm"
                                            href="#">Limpiar Formulario</a>
                                        <a class="smallText text-center" v-if="isEditing"
                                            @click="showDeleteDialog = true" href="#">❌ Eliminar medicamento</a>
                                    </div>
                                </div>
                                <div class="col-md-12" v-else>
                                    <div class="alert alert-warning text-center">
                                        ⚠️ Tu paquete de Medicynia se encuentra expirado. Para continuar utilizando esta funcionalidad, por favor renueva tu suscripción. <br>
                                        <router-link to="/paquetes">Renovar aquí</router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-8 mt-2 mb-4">
                    <h4>Inventario de Medicamentos</h4>
                    <div class="table-responsive">
                        <vs-table class="custom-table">
                            <template #thead>
                                <vs-tr>
                                    <vs-th>Nombre</vs-th>
                                    <vs-th>Nombre Comercial</vs-th>
                                    <vs-th>Presentación</vs-th>
                                    <vs-th>Dosis Usual</vs-th>
                                    <vs-th>Vía de Administración</vs-th>
                                    <vs-th>Acciones</vs-th>
                                </vs-tr>
                            </template>
                            <template #tbody>
                                <vs-tr :key="medicamento.id" v-for="medicamento in medicamentos" :data="medicamento">
                                    <vs-td>{{ medicamento.nombre_medicamento }}</vs-td>
                                    <vs-td>{{ medicamento.nombre_comercial }}</vs-td>
                                    <vs-td>{{ medicamento.presentacion }}</vs-td>
                                    <vs-td>{{ medicamento.dosis_usual || 'No aplica' }}</vs-td>
                                    <vs-td>{{ medicamento.via_administracion || 'No aplica' }}</vs-td>
                                    <vs-td>
                                        <vs-button size="small" color="rgb(59,222,200)" gradient block small
                                            @click="seleccionarMedicamento(medicamento)">
                                            Seleccionar
                                        </vs-button>
                                    </vs-td>
                                </vs-tr>
                            </template>
                        </vs-table>
                    </div>
                    <v-pagination v-model="currentPage" :page-count="totalPages" @input="fetchMedicamentos" />
                </div>

                <vs-dialog v-model="showDeleteDialog" title="Confirmar Eliminación" class="custom-dialog">
                    <div class="container">
                        <div class="row mb-3 mt-3">
                            <div class="col-md-12 text-center">
                                <span class="material-icons md-48 text-danger">warning</span>
                                <h4>Eliminar Medicamento</h4>
                                <p>¿Estás seguro que desea eliminar el medicamento <strong>{{
                                        medicamentoSeleccionado?.nombre_medicamento }}</strong>?</p>
                                <div class="d-flex justify-content-center gap-3">
                                    <vs-button @click="eliminarMedicamento" color="rgb(59,222,200)" gradient>
                                        Si, deseo continuar
                                    </vs-button>
                                    <vs-button @click="showDeleteDialog = false" color="rgb(255,77,77)" gradient>
                                        Cancelar
                                    </vs-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </vs-dialog>

                <div class="col-md-12 mt-5">
                    <h3><span class="material-icons md-24 text-main-gradient">event_note</span> Tipos de Consultas</h3>
                </div>
                <div class="col-md-4 mt-2 mb-4">
                    <h4>{{ consultaSeleccionada ? 'Actualizar Precio' : 'Nuevo Tipo de Consulta' }}</h4>
                    <p class="text-muted">Administra los tipos de consultas que ofreces y sus precios.</p>
                    <div class="card">
                        <div class="card-body">
                            <p class="text-muted mt-2">Los campos marcados con <span class="text-danger">*</span> son obligatorios.</p>
                            <div class="row">
                                <div class="col-md-12 mb-3" v-if="isEditingConsulta">
                                    <label class="form-label">Tipo de Consulta</label>
                                    <input type="text" class="form-control" :value="consultaSeleccionada.tipo_consulta" disabled>
                                </div>
                                <div class="col-md-12 mb-3" v-if="!isEditingConsulta">
                                    <label class="form-label">Tipo de Consulta <span class="text-danger">*</span></label>
                                    <select class="form-control" v-model="nuevaConsulta.tipo_consulta" required>
                                        <option disabled value="">Selecciona un tipo</option>
                                        <option value="en consultorio">En Consultorio</option>
                                        <option value="a domicilio">A Domicilio</option>
                                        <option value="a hospital">A Hospital</option>
                                        <option value="virtual">Virtual</option>
                                        <option value="otra">Otra</option>
                                    </select>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Precio <span class="text-danger">*</span></label>
                                    <input type="number" class="form-control" v-model.number="nuevaConsulta.precio" required>
                                    <small class="form-text text-muted">Ejemplo: 500</small>
                                </div>
                                <div class="col-md-12 text-center" v-if="userStats !== 1">
                                    <vs-button v-if="isEditingConsulta" @click="actualizarPrecioConsulta" color="rgb(59,222,200)" gradient block>
                                        🔄 Actualizar Precio
                                    </vs-button>
                                    <vs-button v-else @click="crearTipoConsulta" color="rgb(59,222,200)" gradient block>
                                        💾 Guardar Tipo de Consulta
                                    </vs-button>
                                    <a class="smallText text-center mt-2 d-block" v-if="isEditingConsulta" @click="resetConsultaForm" href="#">Limpiar Formulario</a>
                                </div>
                                <div class="col-md-12" v-else>
                                    <div class="alert alert-warning text-center">
                                        ⚠️ Tu paquete de Medicynia se encuentra expirado. Para continuar utilizando esta funcionalidad, por favor renueva tu suscripción. <br>
                                        <router-link to="/paquetes">Renovar aquí</router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-8 mt-2 mb-4">
                    <h4>Lista de Tipos de Consultas</h4>
                    <div class="table-responsive">
                        <vs-table class="custom-table">
                            <template #thead>
                                <vs-tr>
                                    <vs-th>Tipo de Consulta</vs-th>
                                    <vs-th>Precio</vs-th>
                                    <vs-th>Acciones</vs-th>
                                </vs-tr>
                            </template>
                            <template #tbody>
                                <vs-tr :key="consulta.id" v-for="consulta in tiposConsulta" :data="consulta">
                                    <vs-td>{{ consulta.tipo_consulta }}</vs-td>
                                    <vs-td>{{ consulta.precio }}</vs-td>
                                    <vs-td>
                                        <vs-button size="small" color="rgb(59,222,200)" gradient block small
                                            @click="seleccionarConsulta(consulta)">
                                            Seleccionar
                                        </vs-button>
                                    </vs-td>
                                </vs-tr>
                            </template>
                        </vs-table>
                    </div>
                </div>

                <div class="col-md-12 mt-5">
                    <h3><span class="material-icons md-24 text-main-gradient">science</span> Tipos de Estudios</h3>
                </div>
                <div class="col-md-12 mt-2 mb-3">
                    <search-estudios :main-id="currentUser?.mainId" @notify="notify"
                        @select="handleEstudioSelected" />
                </div>
                <div class="col-md-4 mt-2 mb-4">
                    <h4>{{ estudioSeleccionado ? 'Editar Tipo de Estudio' : 'Nuevo Tipo de Estudio' }}</h4>
                    <p class="text-muted">Administra los tipos de estudios que puedes realizar a tus pacientes.</p>
                    <div class="card">
                        <div class="card-body">
                            <p class="text-muted mt-2">Los campos marcados con <span class="text-danger">*</span> son obligatorios.</p>
                            <div class="row">
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Nombre del Estudio <span class="text-danger">*</span></label>
                                    <input type="text" class="form-control" v-model.trim="nuevoEstudio.nombre" required>
                                    <small class="form-text text-muted">Ejemplo: Glucosa</small>
                                </div>
                                <div class="col-md-12 mb-3">
                                    <label class="form-label">Unidad de Medida</label>
                                    <input type="text" class="form-control" v-model.trim="nuevoEstudio.unidad">
                                    <small class="form-text text-muted">Ejemplo: mg/dL</small>
                                </div>
                                <div class="col-md-12 text-center" v-if="userStats !== 1">
                                    <vs-button v-if="isEditingEstudio" @click="actualizarTipoEstudio" color="rgb(59,222,200)" gradient block>
                                        🔄 Actualizar Tipo de Estudio
                                    </vs-button>
                                    <vs-button v-else @click="crearTipoEstudio" color="rgb(59,222,200)" gradient block>
                                        💾 Guardar Tipo de Estudio
                                    </vs-button>
                                    <a class="smallText text-center mt-2 d-block" v-if="isEditingEstudio" @click="resetEstudioForm" href="#">Limpiar Formulario</a>
                                </div>
                                <div class="col-md-12" v-else>
                                    <div class="alert alert-warning text-center">
                                        ⚠️ Tu paquete de Medicynia se encuentra expirado. Para continuar utilizando esta funcionalidad, por favor renueva tu suscripción. <br>
                                        <router-link to="/paquetes">Renovar aquí</router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-8 mt-2 mb-4">
                    <h4>Lista de Tipos de Estudios</h4>
                    <div class="table-responsive">
                        <vs-table class="custom-table">
                            <template #thead>
                                <vs-tr>
                                    <vs-th>Nombre</vs-th>
                                    <vs-th>Unidad</vs-th>
                                    <vs-th>Acciones</vs-th>
                                </vs-tr>
                            </template>
                            <template #tbody>
                                <vs-tr :key="estudio.id" v-for="estudio in tiposEstudios" :data="estudio">
                                    <vs-td>{{ estudio.nombre }}</vs-td>
                                    <vs-td>{{ estudio.unidad || 'No especificada' }}</vs-td>
                                    <vs-td>
                                        <vs-button size="small" color="rgb(59,222,200)" gradient block small
                                            @click="seleccionarEstudio(estudio)">
                                            Seleccionar
                                        </vs-button>
                                        <vs-button size="small" color="rgb(255,77,77)" gradient block small
                                            @click="eliminarTipoEstudio(estudio.id)">
                                            Eliminar
                                        </vs-button>
                                    </vs-td>
                                </vs-tr>
                            </template>
                        </vs-table>
                    </div>
                    <v-pagination v-model="currentEstudiosPage" :page-count="totalEstudiosPages" @input="fetchTiposEstudios" />
                </div>

            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import vPagination from 'vue-plain-pagination';
    import SearchMedicamentos from './SearchMedicamentos.vue';
    import SearchEstudios from './SearchEstudios.vue';
    import Horarios from './Horarios.vue';
    var config = require("@/config/api.json");
    const API = config.api;

    export default {
        name: "Medicamentos",
        components: {
            vPagination,
            SearchMedicamentos,
            SearchEstudios,
            Horarios
        },
        data() {
            return {
                currentUser: null,
                nuevoMedicamento: {
                    nombre_medicamento: '',
                    nombre_comercial: '',
                    presentacion: '',
                    dosis_usual: '',
                    via_administracion: ''
                },
                medicamentos: [],
                isEditing: false,
                medicamentoSeleccionado: null,
                currentPage: 1, // Current page
                totalPages: 1, // Total pages
                showDeleteDialog: false, // Controls the visibility of the delete confirmation dialog
                nuevaConsulta: {
                    tipo_consulta: '',
                    precio: null
                },
                tiposConsulta: [],
                isEditingConsulta: false,
                consultaSeleccionada: null,
                nuevoEstudio: {
                    nombre: '',
                    unidad: ''
                },
                tiposEstudios: [],
                isEditingEstudio: false,
                estudioSeleccionado: null,
                currentEstudiosPage: 1, // Página actual para tipos de estudios
                totalEstudiosPages: 1, // Total de páginas para tipos de estudios
                userStats: null // User status
            };
        },
        mounted() {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user) {
                    this.currentUser = user;
                    this.fetchMedicamentos();
                    this.fetchTiposConsulta();
                    this.fetchTiposEstudios();
                    setTimeout(() => {
                        this.userStats = this.getUserStatus();
                    }, 500);
                }
            } catch (error) {
                console.error('Error loading user from localStorage:', error);
            }
        },
        methods: {
            resetForm() {
                this.nuevoMedicamento = {
                    nombre_medicamento: '',
                    nombre_comercial: '',
                    presentacion: '',
                    dosis_usual: '',
                    via_administracion: ''
                };
                this.isEditing = false;
                this.medicamentoSeleccionado = null;
            },
            async handleEstudioSelected(estudio) {
                this.seleccionarEstudio(estudio);
            },
            async crearMedicamento() {
                if (!this.vEmpty(this.nuevoMedicamento.nombre_medicamento, 'Nombre del Medicamento')) return;
                if (!this.vEmpty(this.nuevoMedicamento.nombre_comercial, 'Nombre Comercial')) return;
                if (!this.vEmpty(this.nuevoMedicamento.presentacion, 'Presentación')) return;
                if (!this.vEmpty(this.nuevoMedicamento.dosis_usual, 'Dosis Usual')) return;
                if (!this.vEmpty(this.nuevoMedicamento.via_administracion, 'Vía de Administración')) return;

                try {
                    if (this.currentUser && this.currentUser.mainId) {
                        this.nuevoMedicamento.personal_medico_id = this.currentUser.mainId;
                    } else {
                        this.notify('error', 'Error: Usuario no autenticado');
                        return;
                    }
                    const response = await axios.post(`${API}/crear_medicamento`, this.nuevoMedicamento);

                    if (response.data.status === 'success') {
                        this.notify('success', 'Medicamento creado exitosamente');
                        this.resetForm();
                        this.fetchMedicamentos();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            async actualizarMedicamento() {
                if (!this.medicamentoSeleccionado) return;
                if (!this.vEmpty(this.nuevoMedicamento.nombre_medicamento, 'Nombre del Medicamento')) return;
                if (!this.vEmpty(this.nuevoMedicamento.nombre_comercial, 'Nombre Comercial')) return;
                if (!this.vEmpty(this.nuevoMedicamento.presentacion, 'Presentación')) return;
                if (!this.vEmpty(this.nuevoMedicamento.dosis_usual, 'Dosis Usual')) return;
                if (!this.vEmpty(this.nuevoMedicamento.via_administracion, 'Vía de Administración')) return;

                try {
                    const response = await axios.put(`${API}/actualizar_medicamento/${this.medicamentoSeleccionado.id}`, this.nuevoMedicamento);

                    if (response.data.status === 'success') {
                        this.notify('success', 'Medicamento actualizado exitosamente');
                        this.resetForm();
                        this.fetchMedicamentos();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            async fetchMedicamentos(page = 1) {
                try {
                    const response = await axios.get(`${API}/obtener_medicamentos`, {
                        params: {
                            personal_medico_id: this.currentUser?.mainId,
                            page
                        }
                    });

                    if (response.data.status === 'success') {
                        this.medicamentos = response.data.data; // Assign medications
                        this.currentPage = response.data.currentPage; // Current page
                        this.totalPages = response.data.totalPages; // Total pages
                    } else {
                        this.notify('error', 'Error al obtener los medicamentos');
                    }
                } catch (error) {
                    console.error('Error al obtener los medicamentos:', error);
                    this.notify('error', `Error: ${error.message}`);
                }
            },
            async handleMedicamentoSelected(medicamento) {
                this.seleccionarMedicamento(medicamento);
            },
            seleccionarMedicamento(medicamento) {
                this.medicamentoSeleccionado = null;
                this.nuevoMedicamento = null;
                this.medicamentoSeleccionado = medicamento;
                this.isEditing = true;
                this.nuevoMedicamento = {
                    ...medicamento
                }; // Load selected medicamento data
            },
            async eliminarMedicamento() {
                if (!this.medicamentoSeleccionado) return;

                try {
                    const response = await axios.delete(`${API}/borrar_medicamento/${this.medicamentoSeleccionado.id}`, {
                        data: { personal_medico_id: this.currentUser.mainId }
                    });

                    if (response.data.status === 'success') {
                        this.notify('success', 'Medicamento eliminado exitosamente');
                        this.resetForm();
                        this.fetchMedicamentos();
                    } else {
                        this.notify('error', 'Error al eliminar el medicamento');
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                } finally {
                    this.showDeleteDialog = false;
                }
            },
            resetConsultaForm() {
                this.nuevaConsulta = { tipo_consulta: '', precio: null };
                this.isEditingConsulta = false;
                this.consultaSeleccionada = null;
            },
            async crearTipoConsulta() {
                if (!this.vEmpty(this.nuevaConsulta.tipo_consulta, 'Tipo de Consulta')) return;
                if (!this.vEmpty(this.nuevaConsulta.precio, 'Precio')) return;

                // Validar que no haya más de 4 registros
                if (this.tiposConsulta.length >= 5) {
                    this.notify('error', 'No puedes tener más de 5 tipos de consulta registrados.');
                    return;
                }

                // Validar que no se repita el tipo de consulta
                const tipoExistente = this.tiposConsulta.find(
                    consulta => consulta.tipo_consulta === this.nuevaConsulta.tipo_consulta
                );
                if (tipoExistente) {
                    this.notify('error', `Ya tienes registrado el tipo de consulta "${this.nuevaConsulta.tipo_consulta}".`);
                    return;
                }

                try {
                    if (this.currentUser && this.currentUser.mainId) {
                        this.nuevaConsulta.personal_medico_id = this.currentUser.mainId;
                    } else {
                        this.notify('error', 'Error: Usuario no autenticado');
                        return;
                    }
                    const response = await axios.post(`${API}/registrar_tipo_consulta`, this.nuevaConsulta);

                    if (response.data.status === 'success') {
                        this.notify('success', 'Tipo de consulta registrado exitosamente');
                        this.resetConsultaForm();
                        this.fetchTiposConsulta();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            async fetchTiposConsulta() {
                try {
                    const response = await axios.get(`${API}/listar_tipos_consulta`, {
                        params: { personal_medico_id: this.currentUser?.mainId }
                    });

                    if (response.data.status === 'success') {
                        this.tiposConsulta = response.data.data;
                    } else {
                        this.notify('error', 'Error al obtener los tipos de consulta');
                    }
                } catch (error) {
                    console.error('Error al obtener los tipos de consulta:', error);
                    this.notify('error', `Error: ${error.message}`);
                }
            },
            seleccionarConsulta(consulta) {
                this.consultaSeleccionada = null;
                this.nuevaConsulta = null;
                this.consultaSeleccionada = consulta;
                this.isEditingConsulta = true;
                this.nuevaConsulta = { ...consulta };
            },
            async actualizarPrecioConsulta() {
                if (!this.consultaSeleccionada) return;
                if (!this.vEmpty(this.nuevaConsulta.precio, 'Precio')) return;

                try {
                    const response = await axios.put(`${API}/actualizar_precio_consulta/${this.consultaSeleccionada.id}`, {
                        precio: this.nuevaConsulta.precio,
                        personal_medico_id: this.currentUser.mainId
                    });

                    if (response.data.status === 'success') {
                        this.notify('success', 'Precio de la consulta actualizado exitosamente');
                        this.resetConsultaForm();
                        this.fetchTiposConsulta();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            resetEstudioForm() {
                this.nuevoEstudio = { nombre: '', unidad: '' };
                this.isEditingEstudio = false;
                this.estudioSeleccionado = null;
            },
            async crearTipoEstudio() {
                if (!this.vEmpty(this.nuevoEstudio.nombre, 'Nombre del Estudio')) return;

                try {
                    if (this.currentUser && this.currentUser.mainId) {
                        this.nuevoEstudio.personal_medico_id = this.currentUser.mainId;
                    } else {
                        this.notify('error', 'Error: Usuario no autenticado');
                        return;
                    }
                    const response = await axios.post(`${API}/crear_tipo_estudio`, this.nuevoEstudio);

                    if (response.data.status === 'success') {
                        this.notify('success', 'Tipo de estudio creado exitosamente');
                        this.resetEstudioForm();
                        this.fetchTiposEstudios();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            async fetchTiposEstudios(page = 1) {
                try {
                    const response = await axios.get(`${API}/listar_tipos_estudios`, {
                        params: {
                            personal_medico_id: this.currentUser?.mainId,
                            page
                        }
                    });

                    if (response.data.status === 'success') {
                        this.tiposEstudios = response.data.data; // Asignar los tipos de estudios
                        this.currentEstudiosPage = page; // Página actual
                        this.totalEstudiosPages = Math.ceil(response.data.total / 5); // Calcular total de páginas
                    } else {
                        this.notify('error', 'Error al obtener los tipos de estudios');
                    }
                } catch (error) {
                    console.error('Error al obtener los tipos de estudios:', error);
                    this.notify('error', `Error: ${error.message}`);
                }
            },
            seleccionarEstudio(estudio) {
                this.estudioSeleccionado = null;
                this.nuevoEstudio = null;
                this.estudioSeleccionado = estudio;
                this.isEditingEstudio = true;
                this.nuevoEstudio = { ...estudio };
            },
            async actualizarTipoEstudio() {
                if (!this.estudioSeleccionado) return;
                if (!this.vEmpty(this.nuevoEstudio.nombre, 'Nombre del Estudio')) return;

                try {
                    const response = await axios.put(`${API}/actualizar_tipo_estudio/${this.estudioSeleccionado.id}`, {
                        nombre: this.nuevoEstudio.nombre,
                        unidad: this.nuevoEstudio.unidad,
                        personal_medico_id: this.currentUser?.mainId,
                    });

                    if (response.data.status === 'success') {
                        this.notify('success', 'Tipo de estudio actualizado exitosamente');
                        this.resetEstudioForm();
                        this.fetchTiposEstudios();
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            async eliminarTipoEstudio(id) {
                try {
                    const response = await axios.delete(`${API}/eliminar_tipo_estudio/${id}`, {
                        data: { personal_medico_id: this.currentUser?.mainId }
                    });

                    if (response.data.status === 'success') {
                        this.notify('success', 'Tipo de estudio eliminado exitosamente');
                        this.fetchTiposEstudios();
                    } else {
                        this.notify('error', 'Error al eliminar el tipo de estudio');
                    }
                } catch (error) {
                    this.notify('error', `Error: ${error.response?.data?.message || error.message}`);
                }
            },
            vEmpty(value, fieldName) {
                if (typeof value === 'string' && value.trim() === '') {
                    this.notify('error', `El campo "${fieldName}" es obligatorio.`);
                    return false;
                }
                if (value === null || value === undefined || value === '') {
                    this.notify('error', `El campo "${fieldName}" es obligatorio.`);
                    return false;
                }
                return true;
            }
        }
    };
</script>

<style>
    /* Add any necessary styles here */
</style>